/**
 * 高级JSON修复工具
 * 专门处理AI返回的包含中文字符的JSON内容
 */

export class AdvancedJsonRepair {
  
  /**
   * 修复包含中文字符的JSON
   * @param {string} jsonText - 需要修复的JSON文本
   * @returns {string|null} - 修复后的JSON文本，失败返回null
   */
  static repairJson(jsonText) {
    if (!jsonText || typeof jsonText !== 'string') {
      return null
    }

    try {
      let fixed = jsonText.trim()
      
      // 1. 移除可能的markdown代码块标记
      fixed = this.removeMarkdownCodeBlocks(fixed)
      
      // 2. 清理文本，移除非JSON内容
      fixed = this.cleanJsonText(fixed)
      
      // 3. 修复常见的JSON语法错误
      fixed = this.fixCommonJsonErrors(fixed)
      
      // 4. 处理中文字符和特殊字符
      fixed = this.handleChineseCharacters(fixed)
      
      // 5. 验证和最终修复
      fixed = this.finalValidation(fixed)
      
      return fixed
    } catch (error) {
      console.error('JSON修复过程中出错:', error)
      return null
    }
  }

  /**
   * 移除markdown代码块标记
   */
  static removeMarkdownCodeBlocks(text) {
    // 移除 ```json 和 ``` 标记
    let cleaned = text.replace(/```json\s*/gi, '')
    cleaned = cleaned.replace(/```\s*/g, '')
    
    // 移除可能的语言标识
    cleaned = cleaned.replace(/```\w*\s*/g, '')
    
    return cleaned.trim()
  }

  /**
   * 修复中文引号 - 最简单直接的方法
   */
  static fixChineseQuotes(text) {
    // 第1步：检测并替换所有中文引号
    const hasChinese = /[""''＂＇]/.test(text)
    if (!hasChinese) {
      // 没有中文引号，直接返回
      return text
    }
    
    console.log('🔧 检测到中文引号，开始修复...')
    
    let temp = text
      .replace(/"/g, '"').replace(/"/g, '"')  // 中文双引号
      .replace(/'/g, "'").replace(/'/g, "'")  // 中文单引号
      .replace(/＂/g, '"').replace(/＇/g, "'")  // 全角引号
    
    console.log('  第1步：已替换所有中文引号为英文引号')
    
    // 第2步：逐字符遍历，转义字符串内部的引号
    let result = ''
    let inString = false
    let escapeNext = false
    let escapeCount = 0
    
    for (let i = 0; i < temp.length; i++) {
      const char = temp[i]
      
      // 处理转义
      if (escapeNext) {
        result += char
        escapeNext = false
        continue
      }
      
      if (char === '\\') {
        result += char
        escapeNext = true
        continue
      }
      
      // 检测双引号
      if (char === '"') {
        if (!inString) {
          // 字符串开始
          inString = true
          result += char
        } else {
          // 在字符串内，判断是结束还是内部引号
          // 查看后面的字符
          const next2 = temp.substring(i + 1, i + 3)
          const isStringEnd = /^[\s,}\]\n\r:]|^$/.test(next2)
          
          if (isStringEnd) {
            // 字符串结束
            inString = false
            result += char
          } else {
            // 内部引号，转义
            result += '\\"'
            escapeCount++
          }
        }
      } else {
        result += char
      }
    }
    
    if (escapeCount > 0) {
      console.log(`  第2步：转义了 ${escapeCount} 个字符串内部的引号`)
    }
    console.log('  ✅ 中文引号修复完成')
    
    return result
  }

  /**
   * 清理JSON文本，移除非JSON内容
   */
  static cleanJsonText(text) {
    let cleaned = text.trim()
    
    console.log('🔍 开始清理JSON文本，原始长度:', cleaned.length)
    console.log('📄 原始内容前100字符:', cleaned.substring(0, 100))
    
    // 中文引号已在parseAIResponse中修复，这里不再重复
    
    // 1. 检测JSON类型和边界
    const firstBrace = cleaned.indexOf('{')
    const firstBracket = cleaned.indexOf('[')
    
    let jsonType = ''
    let startIndex = -1
    let endIndex = -1
    
    // 确定JSON类型和开始位置
    if (firstBrace !== -1 && firstBracket !== -1) {
      if (firstBrace < firstBracket) {
        jsonType = 'object'
        startIndex = firstBrace
      } else {
        jsonType = 'array'
        startIndex = firstBracket
      }
    } else if (firstBrace !== -1) {
      jsonType = 'object'
      startIndex = firstBrace
    } else if (firstBracket !== -1) {
      jsonType = 'array'
      startIndex = firstBracket
    } else {
      console.log('❌ 未找到JSON开始标记')
      return null
    }
    
    console.log('🔍 检测到JSON类型:', jsonType, '开始位置:', startIndex)
    
    // 移除前缀
    if (startIndex > 0) {
      console.log('🔧 移除前缀，长度:', startIndex)
      cleaned = cleaned.substring(startIndex)
    }
    
    // 确定结束位置
    if (jsonType === 'object') {
      endIndex = cleaned.lastIndexOf('}')
    } else if (jsonType === 'array') {
      endIndex = cleaned.lastIndexOf(']')
    }
    
    // 移除后缀
    if (endIndex !== -1 && endIndex < cleaned.length - 1) {
      console.log('🔧 移除后缀，长度:', cleaned.length - endIndex - 1)
      cleaned = cleaned.substring(0, endIndex + 1)
    }
    
    // 2. 验证格式正确性
    if (jsonType === 'object') {
      if (!cleaned.startsWith('{') || !cleaned.endsWith('}')) {
        console.log('⚠️ 对象格式不正确，尝试修复')
        const braceStart = cleaned.indexOf('{')
        const braceEnd = cleaned.lastIndexOf('}')
        if (braceStart !== -1 && braceEnd !== -1 && braceEnd > braceStart) {
          cleaned = cleaned.substring(braceStart, braceEnd + 1)
        } else {
          console.log('❌ 无法修复对象格式')
          return null
        }
      }
    } else if (jsonType === 'array') {
      if (!cleaned.startsWith('[') || !cleaned.endsWith(']')) {
        console.log('⚠️ 数组格式不正确，尝试修复')
        const bracketStart = cleaned.indexOf('[')
        const bracketEnd = cleaned.lastIndexOf(']')
        if (bracketStart !== -1 && bracketEnd !== -1 && bracketEnd > bracketStart) {
          cleaned = cleaned.substring(bracketStart, bracketEnd + 1)
        } else {
          console.log('❌ 无法修复数组格式')
          return null
        }
      }
    }
    
    // 3. 验证括号匹配 - 使用智能补充策略
    const openBraces = (cleaned.match(/\{/g) || []).length
    const closeBraces = (cleaned.match(/\}/g) || []).length
    const openBrackets = (cleaned.match(/\[/g) || []).length
    const closeBrackets = (cleaned.match(/\]/g) || []).length
    
    // 智能补充：只在差异较小时补充（避免过度修复）
    const braceDiff = openBraces - closeBraces
    const bracketDiff = openBrackets - closeBrackets
    
    if (braceDiff > 0 && braceDiff <= 3) {
      console.log('⚠️ 大括号不匹配:', { 开: openBraces, 闭: closeBraces, 差异: braceDiff })
      
      // 检查末尾，避免重复补充
      const needsBraces = braceDiff
      let toAdd = needsBraces
      
      // 检查末尾已有多少闭括号
      const endingBraces = (cleaned.match(/\}+$/)?.[0] || '').length
      if (endingBraces > 0) {
        console.log('  末尾已有', endingBraces, '个}')
        toAdd = Math.max(0, needsBraces - endingBraces)
      }
      
      if (toAdd > 0) {
        cleaned += '}'.repeat(toAdd)
        console.log('  🔧 补充了', toAdd, '个闭括号')
      } else {
        console.log('  ✅ 末尾已有足够的闭括号，无需补充')
      }
    } else if (braceDiff > 3) {
      console.warn('⚠️ 大括号差异过大 (' + braceDiff + ')，可能需要specialRepair')
    }
    
    if (bracketDiff > 0 && bracketDiff <= 3) {
      console.log('⚠️ 方括号不匹配:', { 开: openBrackets, 闭: closeBrackets, 差异: bracketDiff })
      
      const needsBrackets = bracketDiff
      let toAdd = needsBrackets
      
      // 检查末尾已有多少闭方括号
      const endingBrackets = (cleaned.match(/\]+$/)?.[0] || '').length
      if (endingBrackets > 0) {
        console.log('  末尾已有', endingBrackets, '个]')
        toAdd = Math.max(0, needsBrackets - endingBrackets)
      }
      
      if (toAdd > 0) {
        cleaned += ']'.repeat(toAdd)
        console.log('  🔧 补充了', toAdd, '个闭方括号')
      } else {
        console.log('  ✅ 末尾已有足够的闭方括号，无需补充')
      }
    } else if (bracketDiff > 3) {
      console.warn('⚠️ 方括号差异过大 (' + bracketDiff + ')，可能需要specialRepair')
    }
    
    // 4. 特殊处理：检查末尾是否缺少逗号后的数组元素
    // 如果末尾是 }}} 这样的结构，可能是对象未闭合但数组也未闭合
    const trimmedEnd = cleaned.trimEnd()
    if (trimmedEnd.endsWith('}') && !trimmedEnd.endsWith('}]') && !trimmedEnd.endsWith('}}')) {
      // 检查是否是shots数组的情况
      if (cleaned.includes('"shots"')) {
        const lastComma = cleaned.lastIndexOf(',')
        const lastCloseBrace = cleaned.lastIndexOf('}')
        // 如果最后的逗号在最后的闭括号之前，说明可能还有未完成的对象
        if (lastComma > 0 && lastComma < lastCloseBrace) {
          // 尝试删除最后一个可能不完整的对象
          const beforeLastObject = cleaned.substring(0, lastComma)
          cleaned = beforeLastObject
          console.log('🔧 移除了可能不完整的最后一个对象')
        }
      }
    }
    
    console.log('✅ JSON清理完成，最终长度:', cleaned.length)
    console.log('📄 清理后前100字符:', cleaned.substring(0, 100))
    
    return cleaned.trim()
  }

  /**
   * 修复常见的JSON语法错误
   */
  static fixCommonJsonErrors(text) {
    let fixed = text
    
    // 1. 修复末尾多余的逗号
    fixed = fixed.replace(/,(\s*[}\]])/g, '$1')
    
    // 2. 修复属性名中的单引号（更安全的方法）
    fixed = fixed.replace(/'([a-zA-Z_][a-zA-Z0-9_]*)':/g, '"$1":')
    
    // 3. 修复缺少的引号（只对英文属性名）
    fixed = fixed.replace(/([^"\\])([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')
    
    // 4. 修复字符串值中未转义的换行符
    fixed = this.fixUnescapedNewlines(fixed)
    
    // 5. 修复连续的逗号
    fixed = fixed.replace(/,\s*,/g, ',')
    
    // 6. 修复对象/数组开头的逗号
    fixed = fixed.replace(/(\[|\{)\s*,/g, '$1')
    
    return fixed
  }

  /**
   * 修复字符串值中未转义的换行符
   */
  static fixUnescapedNewlines(text) {
    let result = ''
    let inString = false
    let escapeNext = false
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i]
      
      if (escapeNext) {
        result += char
        escapeNext = false
        continue
      }
      
      if (char === '\\') {
        result += char
        escapeNext = true
        continue
      }
      
      if (char === '"' && !escapeNext) {
        inString = !inString
        result += char
        continue
      }
      
      // 在字符串内，转义换行符
      if (inString) {
        if (char === '\n') {
          result += '\\n'
          continue
        }
        if (char === '\r') {
          result += '\\r'
          continue
        }
        if (char === '\t') {
          result += '\\t'
          continue
        }
      }
      
      result += char
    }
    
    return result
  }

  /**
   * 处理中文字符和特殊字符
   */
  static handleChineseCharacters(text) {
    let fixed = text
    
    // 确保中文字符在字符串值中正确转义
    // 这里不需要转义中文字符，只需要确保它们在引号内
    
    // 修复可能的编码问题
    try {
      // 尝试重新编码
      fixed = decodeURIComponent(encodeURIComponent(fixed))
    } catch (e) {
      // 如果编码失败，保持原样
    }
    
    return fixed
  }

  /**
   * 转义字符串中的引号
   */
  static escapeQuotesInStrings(text) {
    let result = ''
    let inString = false
    let escapeNext = false
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i]
      const prevChar = i > 0 ? text[i - 1] : ''
      
      if (escapeNext) {
        result += char
        escapeNext = false
        continue
      }
      
      if (char === '\\') {
        result += char
        escapeNext = true
        continue
      }
      
      if (char === '"' && prevChar !== '\\') {
        inString = !inString
        result += char
        continue
      }
      
      if (inString && char === '"' && prevChar !== '\\') {
        // 在字符串中的引号需要转义
        result = result.slice(0, -1) + '\\"'
        continue
      }
      
      result += char
    }
    
    return result
  }

  /**
   * 最终验证和修复
   */
  static finalValidation(text) {
    let fixed = text.trim()
    
    // 检测JSON类型（对象或数组）
    const firstBrace = fixed.indexOf('{')
    const firstBracket = fixed.indexOf('[')
    
    let jsonType = ''
    
    if (firstBrace !== -1 && firstBracket !== -1) {
      if (firstBrace < firstBracket) {
        jsonType = 'object'
      } else {
        jsonType = 'array'
      }
    } else if (firstBrace !== -1) {
      jsonType = 'object'
    } else if (firstBracket !== -1) {
      jsonType = 'array'
    }
    
    // 根据JSON类型进行验证和修复
    if (jsonType === 'object') {
      // 确保对象格式完整
      if (!fixed.startsWith('{')) {
        fixed = '{' + fixed
      }
      if (!fixed.endsWith('}')) {
        fixed = fixed + '}'
      }
    } else if (jsonType === 'array') {
      // 确保数组格式完整
      if (!fixed.startsWith('[')) {
        fixed = '[' + fixed
      }
      if (!fixed.endsWith(']')) {
        fixed = fixed + ']'
      }
    }
    
    // 移除可能的尾随逗号
    fixed = fixed.replace(/,(\s*})/g, '$1')
    fixed = fixed.replace(/,(\s*\])/g, '$1')
    
    return fixed
  }

  /**
   * 尝试解析JSON，如果失败则返回null
   */
  static tryParseJson(jsonText) {
    try {
      return JSON.parse(jsonText)
    } catch (error) {
      console.error('JSON解析失败:', error.message)
      console.log('📄 失败的JSON前100字符:', jsonText.substring(0, 100))
      console.log('📄 失败的JSON后100字符:', jsonText.substring(Math.max(0, jsonText.length - 100)))
      
      // 尝试更详细的错误分析
      this.analyzeJsonError(jsonText, error)
      return null
    }
  }
  
  /**
   * 分析JSON错误并提供修复建议
   */
  static analyzeJsonError(jsonText, error) {
    console.log('🔍 JSON错误分析:')
    console.log('错误信息:', error.message)
    
    // 分析position信息
    const positionMatch = error.message.match(/position (\d+)/)
    if (positionMatch) {
      const position = parseInt(positionMatch[1])
      console.log('错误位置:', position)
      
      // 显示错误位置周围的内容
      const start = Math.max(0, position - 20)
      const end = Math.min(jsonText.length, position + 20)
      const context = jsonText.substring(start, end)
      console.log('错误上下文:', context)
      console.log('错误字符:', jsonText[position] || 'EOF')
      
      // 分析可能的修复方案
      if (position === 1) {
        console.log('💡 建议: 错误在位置1，可能是JSON开头有无效字符')
        console.log('💡 尝试移除第一个字符:', jsonText.substring(1))
      }
    }
    
    // 检查常见问题
    if (jsonText.startsWith('{') === false) {
      console.log('💡 问题: JSON不以{开头')
    }
    if (jsonText.endsWith('}') === false) {
      console.log('💡 问题: JSON不以}结尾')
    }
    
    // 检查引号匹配
    const quoteCount = (jsonText.match(/"/g) || []).length
    if (quoteCount % 2 !== 0) {
      console.log('💡 问题: 引号数量不匹配')
    }
  }

  /**
   * 完整的JSON修复和解析流程
   */
  static repairAndParse(jsonText) {
    console.log('🔧 开始JSON修复流程...')
    console.log('📄 原始文本长度:', jsonText?.length || 0)
    
    const repaired = this.repairJson(jsonText)
    if (!repaired) {
      console.error('❌ JSON修复失败')
      return null
    }
    
    console.log('✅ JSON修复完成，长度:', repaired.length)
    
    const parsed = this.tryParseJson(repaired)
    if (parsed) {
      console.log('✅ JSON解析成功')
      return parsed
    } else {
      console.error('❌ 修复后JSON解析仍然失败')
      
      // 尝试特殊修复方法
      console.log('🔧 尝试特殊修复方法...')
      const specialRepaired = this.specialRepair(jsonText)
      if (specialRepaired) {
        const specialParsed = this.tryParseJson(specialRepaired)
        if (specialParsed) {
          console.log('✅ 特殊修复成功')
          return specialParsed
        }
      }
      
      // 尝试逐步修复 - 识别并修复具体的语法错误
      console.log('🔧 尝试逐步修复...')
      const stepRepaired = this.stepByStepRepair(repaired)
      if (stepRepaired) {
        const stepParsed = this.tryParseJson(stepRepaired)
        if (stepParsed) {
          console.log('✅ 逐步修复成功')
          return stepParsed
        }
      }
      
      // 终极备用方案：尝试提取部分有效的JSON
      console.log('🔧 尝试提取部分有效的JSON...')
      const partialParsed = this.extractValidJson(jsonText)
      if (partialParsed) {
        console.log('✅ 部分JSON提取成功')
        return partialParsed
      }
      
      return null
    }
  }

  /**
   * 逐步修复JSON - 根据错误信息定位并修复
   */
  static stepByStepRepair(jsonText) {
    let fixed = jsonText
    let maxAttempts = 10
    let attempt = 0
    
    while (attempt < maxAttempts) {
      try {
        JSON.parse(fixed)
        // 如果解析成功，返回修复后的JSON
        return fixed
      } catch (error) {
        console.log(`🔧 修复尝试 ${attempt + 1}/${maxAttempts}:`, error.message)
        
        // 分析错误并尝试修复
        const positionMatch = error.message.match(/position (\d+)/)
        if (positionMatch) {
          const position = parseInt(positionMatch[1])
          
          // 获取错误位置的上下文
          const start = Math.max(0, position - 50)
          const end = Math.min(fixed.length, position + 50)
          const context = fixed.substring(start, end)
          const errorChar = fixed[position]
          
          console.log('错误位置:', position, '字符:', errorChar, '上下文:', context)
          
          // 根据错误类型进行修复
          if (error.message.includes('Unexpected non-whitespace character after JSON')) {
            // JSON提前结束，尝试找到真正的结束位置
            console.log('🔧 检测到JSON提前结束，尝试扩展JSON边界...')
            // 这种情况通常是因为前面有语法错误导致解析器提前停止
            // 尝试修复前面的错误
            fixed = this.fixQuoteMismatch(fixed, position)
          } else if (error.message.includes('Expected') && error.message.includes('after property value')) {
            // 缺少逗号或括号
            console.log('🔧 在位置', position, '附近添加逗号...')
            fixed = fixed.substring(0, position) + ',' + fixed.substring(position)
          } else if (error.message.includes('Unexpected token')) {
            // 意外的token，可能是引号问题
            console.log('🔧 修复位置', position, '的引号问题...')
            fixed = this.fixQuoteMismatch(fixed, position)
          } else {
            // 其他错误，尝试删除错误位置的字符
            console.log('🔧 删除错误位置的字符...')
            fixed = fixed.substring(0, position) + fixed.substring(position + 1)
          }
        } else {
          // 无法定位错误位置，放弃
          break
        }
        
        attempt++
      }
    }
    
    return null
  }

  /**
   * 修复引号配对问题
   */
  static fixQuoteMismatch(text, errorPosition) {
    // 从错误位置向前查找，找到最近的字符串开始位置
    let quoteCount = 0
    let lastQuotePos = -1
    
    for (let i = 0; i < errorPosition; i++) {
      if (text[i] === '"' && (i === 0 || text[i - 1] !== '\\')) {
        quoteCount++
        lastQuotePos = i
      }
    }
    
    // 如果引号数量是奇数，说明有未闭合的字符串
    if (quoteCount % 2 !== 0) {
      console.log('🔧 检测到未闭合的字符串，在位置', errorPosition, '添加引号')
      // 在错误位置之前添加引号
      return text.substring(0, errorPosition) + '"' + text.substring(errorPosition)
    }
    
    return text
  }

  /**
   * 提取部分有效的JSON
   */
  static extractValidJson(text) {
    console.log('🔧 尝试提取部分有效的JSON...')
    
    // 尝试逐步缩小JSON范围，直到找到有效的部分
    let cleaned = this.cleanJsonText(text)
    if (!cleaned) {
      return null
    }
    
    // 如果是数组，尝试逐个元素解析
    if (cleaned.startsWith('[')) {
      console.log('🔧 尝试逐个解析数组元素...')
      const elements = this.extractArrayElements(cleaned)
      if (elements && elements.length > 0) {
        console.log(`✅ 成功提取 ${elements.length} 个数组元素`)
        return elements
      }
    }
    
    // 如果是对象，尝试逐个属性解析
    if (cleaned.startsWith('{')) {
      console.log('🔧 尝试逐个解析对象属性...')
      const obj = this.extractObjectProperties(cleaned)
      if (obj && Object.keys(obj).length > 0) {
        console.log(`✅ 成功提取对象，包含 ${Object.keys(obj).length} 个属性`)
        return obj
      }
    }
    
    return null
  }

  /**
   * 提取数组元素
   */
  static extractArrayElements(arrayText) {
    const elements = []
    let depth = 0
    let inString = false
    let escapeNext = false
    let currentElement = ''
    let startIndex = -1
    
    for (let i = 0; i < arrayText.length; i++) {
      const char = arrayText[i]
      
      if (escapeNext) {
        currentElement += char
        escapeNext = false
        continue
      }
      
      if (char === '\\') {
        currentElement += char
        escapeNext = true
        continue
      }
      
      if (char === '"' && !escapeNext) {
        inString = !inString
        currentElement += char
        continue
      }
      
      if (!inString) {
        if (char === '[') {
          if (depth === 0) {
            startIndex = i
          } else {
            currentElement += char
          }
          depth++
        } else if (char === '{') {
          if (depth === 1 && startIndex !== -1) {
            currentElement = '{'
          } else {
            currentElement += char
          }
          depth++
        } else if (char === '}' || char === ']') {
          depth--
          if (char === '}' && depth === 1) {
            currentElement += char
            // 尝试解析这个元素
            try {
              const parsed = JSON.parse(currentElement)
              elements.push(parsed)
              currentElement = ''
            } catch (e) {
              console.log('⚠️ 元素解析失败:', e.message)
              // 继续尝试下一个元素
              currentElement = ''
            }
          } else {
            currentElement += char
          }
        } else if (char === ',' && depth === 1) {
          // 数组元素分隔符
          if (currentElement.trim()) {
            try {
              const parsed = JSON.parse(currentElement)
              elements.push(parsed)
            } catch (e) {
              console.log('⚠️ 元素解析失败:', e.message)
            }
            currentElement = ''
          }
        } else if (depth > 0) {
          currentElement += char
        }
      } else {
        currentElement += char
      }
    }
    
    return elements.length > 0 ? elements : null
  }

  /**
   * 提取对象属性
   */
  static extractObjectProperties(objectText) {
    const obj = {}
    let depth = 0
    let inString = false
    let escapeNext = false
    let currentKey = ''
    let currentValue = ''
    let inKey = false
    let inValue = false
    
    for (let i = 0; i < objectText.length; i++) {
      const char = objectText[i]
      
      if (escapeNext) {
        if (inKey) currentKey += char
        if (inValue) currentValue += char
        escapeNext = false
        continue
      }
      
      if (char === '\\') {
        if (inKey) currentKey += char
        if (inValue) currentValue += char
        escapeNext = true
        continue
      }
      
      if (char === '"' && !escapeNext) {
        if (depth === 1 && !inString && !inValue) {
          inKey = true
          inString = true
        } else if (inKey && inString) {
          inKey = false
          inString = false
        } else if (inValue) {
          inString = !inString
          currentValue += char
        }
        continue
      }
      
      if (inKey) {
        currentKey += char
      } else if (char === ':' && depth === 1 && !inString) {
        inValue = true
      } else if (inValue) {
        if (!inString) {
          if (char === '{' || char === '[') {
            depth++
            currentValue += char
          } else if (char === '}' || char === ']') {
            depth--
            if (depth === 1) {
              // 尝试解析这个属性值
              try {
                const parsed = JSON.parse(currentValue.trim())
                obj[currentKey.trim()] = parsed
                currentKey = ''
                currentValue = ''
                inValue = false
              } catch (e) {
                console.log('⚠️ 属性值解析失败:', e.message)
                currentKey = ''
                currentValue = ''
                inValue = false
              }
            } else {
              currentValue += char
            }
          } else if (char === ',' && depth === 1) {
            // 属性分隔符
            if (currentValue.trim()) {
              try {
                const parsed = JSON.parse(currentValue.trim())
                obj[currentKey.trim()] = parsed
              } catch (e) {
                console.log('⚠️ 属性值解析失败:', e.message)
              }
              currentKey = ''
              currentValue = ''
              inValue = false
            }
          } else {
            currentValue += char
          }
        } else {
          currentValue += char
        }
      } else if (char === '{') {
        depth++
      }
    }
    
    return Object.keys(obj).length > 0 ? obj : null
  }
  
  /**
   * 特殊修复方法，专门处理position 1错误和截断的JSON
   */
  static specialRepair(jsonText) {
    console.log('🔧 开始特殊修复...')
    
    let fixed = jsonText.trim()
    
    // 中文引号已在最开始修复，这里不再重复
    
    // 特殊处理：检查是否包含shots数组但末尾不完整
    if (fixed.includes('"shots"') && fixed.includes('[')) {
      // 查找最后一个完整的对象结束位置
      const shotsMatch = fixed.match(/"shots"\s*:\s*\[/)
      if (shotsMatch) {
        const shotsStartIndex = shotsMatch.index + shotsMatch[0].length
        const restContent = fixed.substring(shotsStartIndex)
        
        // 找到所有完整的shot对象
        const completeShots = []
        let depth = 0
        let currentObj = ''
        let inString = false
        let escapeNext = false
        
        for (let i = 0; i < restContent.length; i++) {
          const char = restContent[i]
          
          if (escapeNext) {
            currentObj += char
            escapeNext = false
            continue
          }
          
          if (char === '\\') {
            escapeNext = true
            currentObj += char
            continue
          }
          
          if (char === '"' && !escapeNext) {
            inString = !inString
          }
          
          if (!inString) {
            if (char === '{') {
              if (depth === 0) {
                currentObj = '{'
              } else {
                currentObj += char
              }
              depth++
            } else if (char === '}') {
              depth--
              currentObj += char
              if (depth === 0 && currentObj.length > 2) {
                // 找到一个完整的对象
                completeShots.push(currentObj.trim())
                currentObj = ''
              }
            } else {
              currentObj += char
            }
          } else {
            currentObj += char
          }
        }
        
        // 如果找到了完整的shot对象，重建JSON
        if (completeShots.length > 0) {
          console.log(`🔧 找到 ${completeShots.length} 个完整的shot对象，重建JSON`)
          const rebuiltShots = completeShots.join(',\n    ')
          fixed = `{\n  "shots": [\n    ${rebuiltShots}\n  ]\n}`
        }
      }
    }
    
    // 检测JSON类型（对象或数组）
    const firstBrace = fixed.indexOf('{')
    const firstBracket = fixed.indexOf('[')
    
    let jsonType = ''
    let startChar = ''
    let endChar = ''
    
    if (firstBrace !== -1 && firstBracket !== -1) {
      if (firstBrace < firstBracket) {
        jsonType = 'object'
        startChar = '{'
        endChar = '}'
      } else {
        jsonType = 'array'
        startChar = '['
        endChar = ']'
      }
    } else if (firstBrace !== -1) {
      jsonType = 'object'
      startChar = '{'
      endChar = '}'
    } else if (firstBracket !== -1) {
      jsonType = 'array'
      startChar = '['
      endChar = ']'
    }
    
    console.log('🔍 特殊修复检测到JSON类型:', jsonType)
    
    // 如果第一个字符不是预期的开始字符，尝试找到正确的开始位置
    if (!fixed.startsWith(startChar)) {
      const startIndex = fixed.indexOf(startChar)
      if (startIndex > 0) {
        console.log('🔧 移除前缀字符，从位置', startIndex, '开始')
        fixed = fixed.substring(startIndex)
      }
    }
    
    // 处理可能的BOM或特殊字符
    const expectedCharCode = jsonType === 'object' ? 123 : 91 // { 或 [
    if (fixed.length > 0 && fixed.charCodeAt(0) !== expectedCharCode) {
      console.log('🔧 第一个字符不是', startChar, '，ASCII码:', fixed.charCodeAt(0))
      // 尝试移除第一个字符
      const withoutFirst = fixed.substring(1)
      if (withoutFirst.startsWith(startChar)) {
        console.log('🔧 移除第一个字符后成功')
        fixed = withoutFirst
      }
    }
    
    // 尝试移除可能的UTF-8 BOM
    if (fixed.startsWith('\uFEFF')) {
      console.log('🔧 移除UTF-8 BOM')
      fixed = fixed.substring(1)
    }
    
    // 处理可能的换行符或空格
    fixed = fixed.replace(/^[\s\n\r\t]+/, '')
    
    // 移除JSON后的多余内容
    const lastEndIndex = fixed.lastIndexOf(endChar)
    if (lastEndIndex !== -1 && lastEndIndex < fixed.length - 1) {
      console.log('🔧 移除JSON后的多余内容')
      fixed = fixed.substring(0, lastEndIndex + 1)
    }
    
    // 确保JSON完整性 - 但不要改变JSON类型
    if (jsonType === 'object') {
      if (!fixed.startsWith('{')) {
        const braceIndex = fixed.indexOf('{')
        if (braceIndex !== -1) {
          fixed = fixed.substring(braceIndex)
        }
      }
      if (!fixed.endsWith('}')) {
        const lastBrace = fixed.lastIndexOf('}')
        if (lastBrace !== -1) {
          fixed = fixed.substring(0, lastBrace + 1)
        }
      }
    } else if (jsonType === 'array') {
      if (!fixed.startsWith('[')) {
        const bracketIndex = fixed.indexOf('[')
        if (bracketIndex !== -1) {
          fixed = fixed.substring(bracketIndex)
        }
      }
      if (!fixed.endsWith(']')) {
        const lastBracket = fixed.lastIndexOf(']')
        if (lastBracket !== -1) {
          fixed = fixed.substring(0, lastBracket + 1)
        }
      }
    }
    
    console.log('🔧 特殊修复完成，新长度:', fixed.length)
    console.log('📄 修复后前50字符:', fixed.substring(0, 50))
    
    return fixed
  }
  

  /**
   * 从AI响应中提取JSON内容
   */
  static extractJsonFromResponse(response) {
    if (!response || typeof response !== 'string') {
      return null
    }

    // 方法1: 查找代码块
    const codeBlockMatch = response.match(/```(?:json)?\s*([\s\S]*?)\s*```/)
    if (codeBlockMatch) {
      console.log('📝 方法1: 从代码块提取JSON')
      return codeBlockMatch[1].trim()
    }

    // 方法2: 查找JSON对象
    const jsonMatch = response.match(/\{[\s\S]*\}/)
    if (jsonMatch) {
      console.log('📝 方法2: 从文本中提取JSON对象')
      return jsonMatch[0].trim()
    }

    // 方法3: 查找可能的JSON数组
    const arrayMatch = response.match(/\[[\s\S]*\]/)
    if (arrayMatch) {
      console.log('📝 方法3: 从文本中提取JSON数组')
      return arrayMatch[0].trim()
    }

    console.log('⚠️ 未找到JSON格式内容')
    return null
  }

  /**
   * 智能JSON提取和修复
   */
  static smartExtractAndRepair(response) {
    console.log('🔍 开始智能JSON提取...')
    
    const extracted = this.extractJsonFromResponse(response)
    if (!extracted) {
      console.log('❌ 无法提取JSON内容')
      return null
    }
    
    console.log('📄 提取的JSON文本前200字符:', extracted.substring(0, 200))
    
    return this.repairAndParse(extracted)
  }
}

export default AdvancedJsonRepair

